import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { taskAPI, projectAPI, stageGateAPI, testCaseAPI } from '../../api/services';
import { Task, TaskComment, User } from '../../types';
import { useAppSelector } from '../../store/hooks';

// Resolve attachment URLs — /uploads/file.pdf → https://backend/uploads/file.pdf
const resolveFileUrl = (url: string) => {
  if (!url) return url;
  if (url.startsWith('http')) return url;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  return base ? `${base}${url}` : url;
};

const TaskDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [project, setProject] = useState<any>(null);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [editingSubtaskTitle, setEditingSubtaskTitle] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentType, setAttachmentType] = useState<'file' | 'link'>('link');
  const [attachmentLink, setAttachmentLink] = useState('');
  const [attachmentName, setAttachmentName] = useState('');
  const [blockerForm, setBlockerForm] = useState({ type: 'external', description: '' });
  const [blockerSubmitting, setBlockerSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; newStatus: string; isDone: boolean; isRevert: boolean }>({ show: false, newStatus: '', isDone: false, isRevert: false });
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [taskGates, setTaskGates] = useState<any[]>([]);
  const [taskTestCases, setTaskTestCases] = useState<any[]>([]);
  const [showCreateTest, setShowCreateTest] = useState<string | null>(null);
  const [newTest, setNewTest] = useState({ title: '', description: '', assignedToUserId: '', assignedToName: '', steps: [{ step: '', expectedResult: '' }] });
  const [executeTestId, setExecuteTestId] = useState<string | null>(null);
  const [executeNotes, setExecuteNotes] = useState('');
  const [approveRejectNotes, setApproveRejectNotes] = useState('');
  const { user: gateUser } = useAppSelector(s => s.auth);
  const gateUserId = gateUser?.id || (gateUser as any)?._id;
  const gateOrgPriority = (gateUser as any)?.organizationRole?.priority || 0;
  const [expandedGate, setExpandedGate] = useState<string | null>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (id) {
      fetchTaskDetails(true);
      fetchComments();
      fetchUsers();
      fetchGatesAndTests();
    }
  }, [id]);

  useEffect(() => {
    if (task?.projectId) {
      fetchProject();
    }
  }, [task?.projectId]);

  const fetchGatesAndTests = async () => {
    if (!id) return;
    try {
      const [gatesRes, testsRes] = await Promise.all([
        stageGateAPI.getTaskGates(id).catch(() => ({ data: { data: [] } })),
        testCaseAPI.getByTask(id).catch(() => ({ data: { data: [] } })),
      ]);
      setTaskGates(gatesRes.data.data || []);
      setTaskTestCases(testsRes.data.data || []);
    } catch {
      // silently fail
    }
  };

  const fetchTaskDetails = async (showSpinner = false) => {
    try {
      if (showSpinner) setLoading(true);
      const response = await taskAPI.getTaskById(id!);
      setTask(response.data.data);
    } catch (err: any) {
      console.error('Error fetching task:', err);
      setError(err.response?.data?.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await taskAPI.getComments(id!);
      setComments(response.data.data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await projectAPI.getProjectUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchProject = async () => {
    if (!task?.projectId) return;
    
    try {
      const response = await projectAPI.getProjectById(task.projectId);
      setProject(response.data.data);
    } catch (err) {
      console.error('Error fetching project:', err);
    }
  };

  const updateTaskField = async (field: string, value: any) => {
    if (!task) return;

    try {
      await taskAPI.updateTask(task._id, { [field]: value });
      await fetchTaskDetails();
      if (field === 'status') setError('');
    } catch (err: any) {
      console.error('Error updating task:', err);
      const msg = err.response?.data?.message || 'Failed to update task';
      if (field === 'status') { setError(msg); } else { alert(msg); }
    }
  };

  const handleTitleChange = async (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const newTitle = e.target.value.trim();
    if (newTitle && newTitle !== task?.title) {
      await updateTaskField('title', newTitle);
    }
  };

  const handleDescriptionChange = async (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const newDescription = e.target.value.trim();
    if (newDescription !== task?.description) {
      await updateTaskField('description', newDescription);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) {
      setErrors(prev => ({ ...prev, comment: 'Comment cannot be empty' }));
      return;
    }
    setErrors(prev => ({ ...prev, comment: '' }));

    try {
      setIsAddingComment(true);
      await taskAPI.addComment(id!, { content: newComment });
      setNewComment('');
      await fetchComments();
      await fetchTaskDetails();
    } catch (err: any) {
      console.error('Error adding comment:', err);
      setErrors(prev => ({ ...prev, comment: 'Failed to add comment' }));
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!window.confirm('Are you sure you want to delete this task? This cannot be undone.')) return;
    try {
      await taskAPI.deleteTask(id!);
      navigate(-1);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleRenameSubtask = async (subtaskId: string, newTitle: string) => {
    if (!newTitle.trim()) return;
    const updatedSubtasks = task!.subtasks.map(st =>
      st._id === subtaskId ? { ...st, title: newTitle.trim() } : st
    );
    setTask(prev => prev ? { ...prev, subtasks: updatedSubtasks } : prev);
    try {
      await taskAPI.updateTask(id!, { subtasks: updatedSubtasks } as any);
    } catch { /* ignore */ }
  };

  const handleToggleSubtask = async (subtaskId: string, completed: boolean) => {
    // Optimistic update
    setTask(prev => prev ? {
      ...prev,
      subtasks: prev.subtasks.map(st => st._id === subtaskId ? { ...st, completed: !completed } : st),
    } : prev);
    try {
      await taskAPI.updateSubtask(id!, subtaskId, !completed);
    } catch (err) {
      // Revert
      setTask(prev => prev ? {
        ...prev,
        subtasks: prev.subtasks.map(st => st._id === subtaskId ? { ...st, completed } : st),
      } : prev);
    }
  };

  const handleAddSubtask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSubtaskTitle.trim()) {
      setErrors(prev => ({ ...prev, subtask: 'Subtask title is required' }));
      return;
    }
    if (newSubtaskTitle.trim().length < 3) {
      setErrors(prev => ({ ...prev, subtask: 'Subtask title must be at least 3 characters' }));
      return;
    }
    setErrors(prev => ({ ...prev, subtask: '' }));

    try {
      await taskAPI.createSubtask(id!, newSubtaskTitle.trim());
      setNewSubtaskTitle('');
      setShowAddSubtask(false);
      await fetchTaskDetails();
    } catch (err: any) {
      console.error('Error adding subtask:', err);
      const msg: string = err.response?.data?.message || '';
      const display = msg.includes('Cast to ObjectId failed')
        ? 'Invalid reference — please refresh and try again.'
        : msg || 'Failed to add subtask';
      setErrors(prev => ({ ...prev, subtask: display }));
    }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    if (!window.confirm('Are you sure you want to delete this subtask?')) return;
    const prev = task?.subtasks;
    setTask(t => t ? { ...t, subtasks: t.subtasks.filter(st => st._id !== subtaskId) } : t);
    try {
      await taskAPI.deleteSubtask(id!, subtaskId);
    } catch {
      setTask(t => t && prev ? { ...t, subtasks: prev } : t);
    }
  };

  const handleAddAttachment = async (name: string, url: string, type: string) => {
    try {
      setUploadingFile(true);
      await taskAPI.addAttachment(id!, { name, url, type });
      await fetchTaskDetails();
      setShowAttachmentModal(false);
      setAttachmentLink('');
      setAttachmentName('');
      setAttachmentType('link');
    } catch (err: any) {
      console.error('Error adding attachment:', err);
      alert(err.response?.data?.message || 'Failed to add attachment');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteAttachment = async (url: string) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      await taskAPI.deleteAttachment(id!, url);
      await fetchTaskDetails();
    } catch (err: any) {
      console.error('Error deleting attachment:', err);
      alert(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setUploadingFile(true);

      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Upload file to server
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const uploadResponse = await fetch(`${apiBase}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      const uploadData = await uploadResponse.json();
      const fileUrl = uploadData.url || uploadData.data?.url;

      if (!fileUrl) {
        throw new Error('No file URL returned from server');
      }

      // Determine file type
      let fileType = 'file';
      if (file.type.startsWith('image/')) {
        fileType = 'image';
      } else if (file.type.includes('pdf') || file.type.includes('document')) {
        fileType = 'document';
      }

      // Add attachment with file URL
      await handleAddAttachment(file.name, fileUrl, fileType);
    } catch (err: any) {
      console.error('Error uploading file:', err);
      alert(err.message || 'Failed to upload file');
      setUploadingFile(false);
    }
  };

  const handleLinkSubmit = () => {
    if (!attachmentLink.trim()) {
      alert('Please enter a link URL');
      return;
    }

    const name = attachmentName.trim() || attachmentLink;
    handleAddAttachment(name, attachmentLink.trim(), 'link');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleQuickStatusChange = async (newStatus: string) => {
    if (!id || !task || task.status === newStatus) return;
    if (newStatus === 'done' || (task.status === 'done' && newStatus !== 'done')) {
      setConfirmModal({ show: true, newStatus, isDone: newStatus === 'done', isRevert: task.status === 'done' });
      return;
    }
    const oldStatus = task.status;
    setTask(prev => prev ? { ...prev, status: newStatus as any } : prev);
    try { await taskAPI.updateStatus(id, newStatus); }
    catch (err: any) { setTask(prev => prev ? { ...prev, status: oldStatus } : prev); setError(err.response?.data?.message || 'Failed'); }
  };

  const confirmStatusChange = async () => {
    if (!id || !task) return;
    const { newStatus } = confirmModal;
    const oldStatus = task.status;
    setConfirmModal({ show: false, newStatus: '', isDone: false, isRevert: false });
    setTask(prev => prev ? { ...prev, status: newStatus as any } : prev);
    try { await taskAPI.updateStatus(id, newStatus); }
    catch (err: any) { setTask(prev => prev ? { ...prev, status: oldStatus } : prev); setError(err.response?.data?.message || 'Failed'); }
  };

  const handleAssignUser = async (userId: string) => {
    try {
      await taskAPI.assignTask(id!, userId);
      await fetchTaskDetails();
      setShowAssigneeDropdown(false);
      setAssigneeSearch('');
    } catch (err: any) {
      console.error('Error assigning user:', err);
      alert(err.response?.data?.message || 'Failed to assign user');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      todo: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
      in_progress: 'bg-blue-100 text-blue-600 hover:bg-blue-200',
      blocked: 'bg-red-100 text-red-600 hover:bg-red-200',
      review: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      done: 'bg-green-100 text-green-600 hover:bg-green-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-600 hover:bg-gray-200';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'text-gray-500',
      medium: 'text-blue-600',
      high: 'text-orange-600',
      critical: 'text-red-600',
    };
    return colors[priority] || 'text-gray-500';
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  // Helper functions for project member validation
  const isProjectMember = (userId: string) => {
    if (!project) return false;
    
    // Check if user is project owner
    if (project.owner.userId === userId) return true;
    
    // Check if user is in project members
    return project.members.some((member: any) => member.userId === userId);
  };

  const getProjectMembers = () => {
    if (!project) return [];
    
    const projectUserIds = [
      project.owner.userId,
      ...project.members.map((member: any) => member.userId)
    ];
    
    return users.filter(user => projectUserIds.includes(user._id));
  };

  const getNonProjectMembers = () => {
    if (!project) return users;
    
    const projectUserIds = [
      project.owner.userId,
      ...project.members.map((member: any) => member.userId)
    ];
    
    return users.filter(user => !projectUserIds.includes(user._id));
  };

  const filteredProjectMembers = getProjectMembers().filter(user =>
    user.name.toLowerCase().includes(assigneeSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const filteredNonProjectMembers = getNonProjectMembers().filter(user =>
    user.name.toLowerCase().includes(assigneeSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex h-screen">
        <div className="w-14 bg-gray-900 shrink-0 hidden lg:block" />
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Task not found'}
          </div>
          <button
            onClick={() => navigate('/employee/projects')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between sticky top-14 z-10">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </button>
          <span className="text-xs text-gray-400 capitalize">{task.parentType}</span>
          <span className="text-xs text-gray-300">·</span>
          <span className="text-xs text-gray-400">{new Date(task.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          {(task.storyPoints ?? 0) > 0 && <><span className="text-xs text-gray-300">·</span><span className="text-xs text-blue-600 font-medium">{task.storyPoints} SP</span></>}
        </div>
        <button onClick={handleDeleteTask} className="text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
          Delete
        </button>
      </div>

      {/* Project Approval Status Warning for Tasks */}
      {project && (
        <>
          {project.approvalStatus === 'pending' && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-8 mt-6">
              <div className="flex items-start">
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Task in Unapproved Project</strong>
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    This task belongs to "{project.name}" which is pending approval. Work may need to be revised if project is rejected.
                  </p>
                  <button 
                    className="mt-2 text-xs text-yellow-800 hover:text-yellow-900 underline"
                    onClick={() => navigate(`/employee/projects/${project._id}`)}
                  >
                    View project status →
                  </button>
                </div>
              </div>
            </div>
          )}

          {project.approvalStatus === 'rejected' && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-8 mt-6">
              <div className="flex items-start">
                <div className="ml-3">
                  <p className="text-sm text-red-800">
                    <strong>Task in Rejected Project</strong>
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    ⚠ This task belongs to "{project.name}" which has been rejected. Consider pausing work until project issues are resolved.
                  </p>
                  <button 
                    className="mt-2 text-xs text-red-800 hover:text-red-900 underline"
                    onClick={() => navigate(`/employee/projects/${project._id}`)}
                  >
                    View project feedback →
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="max-w-5xl mx-auto">
      {/* Quick Status Bar */}
      <div className="flex items-center gap-1 bg-white border-b border-gray-100 px-4 py-2">
        {[
          { value: 'todo', label: 'To Do', color: 'gray' },
          { value: 'in_progress', label: 'In Progress', color: 'blue' },
          { value: 'blocked', label: 'Blocked', color: 'red' },
          { value: 'review', label: 'Review', color: 'yellow' },
          { value: 'done', label: 'Done', color: 'green' },
        ].map(s => {
          const isActive = task.status === s.value;
          const colorMap: Record<string, string> = {
            gray: isActive ? 'bg-gray-600 text-white' : 'text-gray-500 hover:bg-gray-100',
            blue: isActive ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50',
            red: isActive ? 'bg-red-500 text-white' : 'text-red-500 hover:bg-red-50',
            yellow: isActive ? 'bg-yellow-500 text-white' : 'text-yellow-600 hover:bg-yellow-50',
            green: isActive ? 'bg-green-600 text-white' : 'text-green-600 hover:bg-green-50',
          };
          return (
            <button key={s.value} onClick={() => handleQuickStatusChange(s.value)}
              className={`px-3 py-1 rounded-full text-[10px] font-medium transition-colors ${colorMap[s.color]}`}>
              {s.value === 'done' && isActive ? '✓ ' : ''}{s.label}
            </button>
          );
        })}
      </div>

      <div className="flex">
        {/* Main Content */}
        <div className="flex-1 px-6 py-4 bg-white min-h-[calc(100vh-120px)]">
          {/* Task Title */}
          <textarea
            ref={titleRef}
            defaultValue={task.title}
            onBlur={handleTitleChange}
            placeholder="Task title"
            className="w-full text-xl font-semibold text-gray-900 resize-none border-none focus:outline-none focus:ring-0 p-0 mb-4"
            rows={1}
            onInput={(e) => {
              e.currentTarget.style.height = 'auto';
              e.currentTarget.style.height = e.currentTarget.scrollHeight + 'px';
            }}
          />

          {/* Properties — compact inline rows */}
          <div className="space-y-2 mb-6 text-sm">
            {/* Status + Priority */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 w-24 text-xs text-gray-400">
                <div className={`w-2 h-2 rounded-full ${
                  task.status === 'done' ? 'bg-green-500' : task.status === 'in_progress' ? 'bg-blue-500'
                  : task.status === 'blocked' ? 'bg-red-500' : task.status === 'review' ? 'bg-yellow-500' : 'bg-gray-400'
                }`} />
                Status
              </div>
              <div className="flex gap-1">
                {['todo', 'in_progress', 'blocked', 'review', 'done'].map(s => (
                  <button key={s} onClick={() => updateTaskField('status', s as any)}
                    className={`text-[10px] px-2 py-0.5 rounded capitalize ${task.status === s ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-400 hover:text-gray-600'}`}>
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                <svg className="w-4 h-4 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <span className="flex-1">{error}</span>
                <button onClick={() => setError('')} className="text-amber-400 hover:text-amber-600">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            )}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 w-24 text-xs text-gray-400">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 11l5-5m0 0l5 5m-5-5v12" /></svg>
                Priority
              </div>
              <div className="flex gap-1">
                {['low', 'medium', 'high', 'critical'].map(p => (
                  <button key={p} onClick={() => updateTaskField('priority', p as any)}
                    className={`text-[10px] px-2 py-0.5 rounded capitalize ${task.priority === p ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-400 hover:text-gray-600'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Assignees */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 w-24 text-xs text-gray-400">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                Assignees
              </div>
              <div className="flex items-center gap-1.5 flex-1 flex-wrap">
                {task.assignedTo && task.assignedTo.length > 0 && task.assignedTo.map((assignee, idx) => (
                  <span key={idx} className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1 group/assignee">
                    <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[8px] flex items-center justify-center">{assignee.name.charAt(0).toUpperCase()}</span>
                    {assignee.name.split(' ')[0]}
                    <button onClick={() => taskAPI.unassignTask(id!, assignee.userId).then(() => fetchTaskDetails(false)).catch(() => {})}
                      className="w-3.5 h-3.5 rounded-full hover:bg-blue-200 flex items-center justify-center opacity-0 group-hover/assignee:opacity-100 transition-opacity -mr-0.5"
                      title={`Remove ${assignee.name}`}>
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                ))}
                <div className="relative">
                  <button onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                    className="text-[10px] text-gray-400 hover:text-blue-600 transition-colors">+ Add</button>
                  {showAssigneeDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      <div className="p-2">
                        <input
                          type="text"
                          placeholder="Search or enter email..."
                          value={assigneeSearch}
                          onChange={(e) => setAssigneeSearch(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {/* Project Members Section */}
                        {filteredProjectMembers.length > 0 && (
                          <>
                            <div className="px-3 py-2 bg-blue-50 text-xs font-medium text-blue-700 border-b border-blue-100">
                              Project Team Members
                            </div>
                            {filteredProjectMembers.map((user: any) => (
                              <button
                                key={user._id}
                                onClick={() => handleAssignUser(user._id!)}
                                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                              >
                                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium">{user.name}</div>
                                  <div className="text-xs text-gray-500">{user.email}</div>
                                </div>
                                <div className="text-xs text-green-600">✓ Member</div>
                              </button>
                            ))}
                          </>
                        )}

                        {/* Non-Project Members Section */}
                        {filteredNonProjectMembers.length > 0 && (
                          <>
                            {filteredProjectMembers.length > 0 && (
                              <div className="border-t border-gray-200 my-1"></div>
                            )}
                            <div className="px-3 py-2 bg-yellow-50 text-xs font-medium text-yellow-700 border-b border-yellow-100">
                              Other Users (Not in Project Team)
                            </div>
                            {filteredNonProjectMembers.map((user: any) => (
                              <button
                                key={user._id}
                                onClick={() => {
                                  if (window.confirm(
                                    `${user.name} is not a member of this project team.\n\nAssigning tasks to non-team members may limit their access to project resources.\n\nConsider adding them to the project team first.\n\nDo you still want to assign this task to ${user.name}?`
                                  )) {
                                    handleAssignUser(user._id!);
                                  }
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-yellow-50 flex items-center gap-2 text-sm border-l-2 border-yellow-400"
                              >
                                <div className="w-6 h-6 rounded-full bg-gray-500 text-white flex items-center justify-center text-xs font-medium">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium">{user.name}</div>
                                  <div className="text-xs text-gray-500">{user.email}</div>
                                  <div className="text-xs text-yellow-600 mt-0.5">⚠ Not in project team</div>
                                </div>
                              </button>
                            ))}
                          </>
                        )}

                        {/* No Results */}
                        {filteredProjectMembers.length === 0 && filteredNonProjectMembers.length === 0 && (
                          <div className="px-3 py-4 text-center text-gray-500 text-sm">
                            No users found matching "{assigneeSearch}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Project Team Helper */}
            {project && (
              <div className="flex items-center gap-3 col-span-2">
                <div className="flex items-center gap-2 text-gray-600 w-32">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121L17 20zM21 16v-4a3 3 0 00-3-3h-1.138a6.002 6.002 0 00-6.724 0H12a3 3 0 00-3 3v4h12z" />
                  </svg>
                  <span>Project Team</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <span>{project.members.length + 1} members</span>
                    <span>•</span>
                    <button
                      onClick={() => {
                        const projectPath = task?.projectId ? `/employee/projects/${task.projectId}` : '/employee/projects';
                        navigate(projectPath);
                      }}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Manage team
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Due Date + SP */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 w-24 text-xs text-gray-400">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                Due Date
              </div>
              <input type="date" value={task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : ''}
                onChange={(e) => updateTaskField('dueDate', e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-400" />
              <div className="flex items-center gap-2 ml-4 text-xs text-gray-400">SP</div>
              <input type="number" min={0} value={task.storyPoints || ''} onChange={e => updateTaskField('storyPoints', Number(e.target.value) || 0)}
                className="w-14 text-xs border border-gray-200 rounded-lg px-2 py-1 text-right focus:outline-none focus:border-blue-400" placeholder="0" />
            </div>

            {/* Tags */}
            {task.tags && task.tags.length > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 w-24 text-xs text-gray-400">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                  Tags
                </div>
                <div className="flex flex-wrap gap-1">
                  {task.tags.map((tag, idx) => (
                    <span key={idx} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">Description</div>
            <textarea
              defaultValue={task.description}
              onBlur={handleDescriptionChange}
              placeholder="Add a description..."
              className="w-full px-3 py-2 text-xs text-gray-700 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 resize-none"
              rows={3}
            />
          </div>

          {/* Subtasks */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                Subtasks {task.subtasks && task.subtasks.length > 0 && (
                  <span className="text-gray-400 font-normal">
                    ({task.subtasks.filter(st => st.completed).length}/{task.subtasks.length})
                  </span>
                )}
              </div>
              {!showAddSubtask && (
                <button onClick={() => setShowAddSubtask(true)} className="text-[10px] text-gray-400 hover:text-blue-600">+ Add subtask</button>
              )}
            </div>

            {showAddSubtask && (
              <form onSubmit={handleAddSubtask} className="mb-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSubtaskTitle}
                    onChange={(e) => { setNewSubtaskTitle(e.target.value); setErrors(prev => ({ ...prev, subtask: '' })); }}
                    placeholder="Subtask title..."
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSubtask(false);
                      setNewSubtaskTitle('');
                      setErrors(prev => ({ ...prev, subtask: '' }));
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
                {errors.subtask && <p className="mt-1 text-xs text-red-600">{errors.subtask}</p>}
              </form>
            )}

            {task.subtasks && task.subtasks.length > 0 && (
              <div className="space-y-1">
                {task.subtasks.map((subtask) => (
                  <div key={subtask._id} className="border border-gray-100 rounded-lg p-2 hover:bg-gray-50 group">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={subtask.completed}
                        onChange={() => handleToggleSubtask(subtask._id, subtask.completed)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      {editingSubtaskId === subtask._id ? (
                        <input
                          autoFocus
                          value={editingSubtaskTitle}
                          onChange={e => setEditingSubtaskTitle(e.target.value)}
                          onBlur={() => { handleRenameSubtask(subtask._id, editingSubtaskTitle); setEditingSubtaskId(null); }}
                          onKeyDown={e => { if (e.key === 'Enter') { handleRenameSubtask(subtask._id, editingSubtaskTitle); setEditingSubtaskId(null); } if (e.key === 'Escape') setEditingSubtaskId(null); }}
                          className="flex-1 text-sm border border-blue-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <span
                          className={`flex-1 text-sm cursor-pointer ${subtask.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}
                          onDoubleClick={() => { setEditingSubtaskId(subtask._id); setEditingSubtaskTitle(subtask.title); }}
                          title="Double-click to edit"
                        >
                          {subtask.title}
                        </span>
                      )}
                      <label className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:bg-gray-100 rounded cursor-pointer transition-opacity" title="Attach file">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <input type="file" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file || !id) return;
                          const fd = new FormData();
                          fd.append('file', file);
                          try {
                            const token = localStorage.getItem('token');
                            const apiBase = process.env.REACT_APP_API_URL || '/api';
                            const uploadRes = await fetch(`${apiBase}/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                            const uploadData = await uploadRes.json();
                            const fileUrl = uploadData.url || uploadData.data?.url;
                            if (!fileUrl) return;
                            await taskAPI.addSubtaskAttachment(id, subtask._id, { name: file.name, url: fileUrl, type: file.type.startsWith('image') ? 'image' : 'file' });
                            fetchTaskDetails();
                          } catch { /* error */ }
                        }} />
                      </label>
                      <button
                        onClick={() => handleDeleteSubtask(subtask._id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 rounded transition-opacity"
                        title="Delete subtask"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    {/* Subtask attachments */}
                    {(subtask.attachments ?? []).length > 0 && (
                      <div className="ml-7 mt-1.5 space-y-1">
                        {(subtask.attachments ?? []).map((att, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs group/att">
                            {att.type === 'image' ? (
                              <img src={resolveFileUrl(att.url)} alt={att.name}
                                onClick={() => setLightboxUrl(resolveFileUrl(att.url))}
                                className="w-8 h-8 rounded object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity" />
                            ) : (
                              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            )}
                            <a href={resolveFileUrl(att.url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{att.name}</a>
                            <a href={resolveFileUrl(att.url)} download={att.name} className="text-gray-400 hover:text-blue-600" title="Download" onClick={e => e.stopPropagation()}>
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </a>
                            <button
                              onClick={async () => { if (!id) return; await taskAPI.deleteSubtaskAttachment(id, subtask._id, att.url); fetchTaskDetails(); }}
                              className="opacity-0 group-hover/att:opacity-100 text-red-500 hover:text-red-700"
                              title="Remove"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(!task.subtasks || task.subtasks.length === 0) && !showAddSubtask && (
              <p className="text-sm text-gray-500">No subtasks yet</p>
            )}
          </div>

          {/* Gates & Tests */}
          {(taskGates.length > 0 || taskTestCases.length > 0) && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Gates & Tests
                {taskGates.filter((g: any) => g.status === 'pending').length > 0 && (
                  <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-normal normal-case">
                    {taskGates.filter((g: any) => g.status === 'pending').length} pending
                  </span>
                )}
              </h3>

              {taskGates.length > 0 && (
                <div className="space-y-2 mb-3">
                  {taskGates.map((gate: any) => {
                    const statusColors: Record<string, string> = {
                      pending: 'border-yellow-200 bg-yellow-50',
                      approved: 'border-green-200 bg-green-50',
                      rejected: 'border-red-200 bg-red-50',
                      passed: 'border-green-200 bg-green-50',
                      failed: 'border-red-200 bg-red-50',
                      skipped: 'border-gray-200 bg-gray-50',
                    };
                    const color = statusColors[gate.status] || statusColors.pending;
                    const isExpanded = expandedGate === gate._id;
                    const gateTestCases = taskTestCases.filter((tc: any) => tc.stageGateRequestId === gate._id);

                    return (
                      <div key={gate._id} className={`border rounded-lg overflow-hidden ${color}`}>
                        <div
                          className="px-3 py-2 cursor-pointer"
                          onClick={() => setExpandedGate(isExpanded ? null : gate._id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <span className="text-sm font-medium capitalize">{gate.gateType}</span>
                              <span className="text-xs text-gray-500">→ {gate.stage}</span>
                              {gate.mandatory && (
                                <span className="text-[10px] font-semibold bg-red-600 text-white px-1.5 py-0.5 rounded">Required</span>
                              )}
                            </div>
                            <span className={`text-xs font-medium capitalize px-2 py-0.5 rounded-full ${
                              gate.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                              gate.status === 'approved' || gate.status === 'passed' ? 'bg-green-200 text-green-800' :
                              gate.status === 'rejected' || gate.status === 'failed' ? 'bg-red-200 text-red-800' :
                              'bg-gray-200 text-gray-600'
                            }`}>{gate.status}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 ml-5">
                            {gate.assignedTo?.name && <span>Assigned to {gate.assignedTo.name}</span>}
                            {gate.gateType === 'testing' && <span>{gateTestCases.length} test{gateTestCases.length !== 1 ? 's' : ''}</span>}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="border-t px-3 py-2 bg-white/60 space-y-2">
                            {gate.notes && (
                              <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                                <span className="font-medium">Notes:</span> {gate.notes}
                              </div>
                            )}

                            {gate.resolutionNotes && (
                              <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                                <span className="font-medium">Resolution:</span> {gate.resolutionNotes}
                                {gate.resolvedBy?.name && <span className="text-gray-400"> — {gate.resolvedBy.name}</span>}
                              </div>
                            )}

                            {/* Approval gate actions */}
                            {gate.gateType === 'approval' && gate.status === 'pending' && (() => {
                              const isGateRequester = gate.requestedBy?.userId && gateUserId && String(gate.requestedBy.userId) === String(gateUserId);
                              if (isGateRequester && gateOrgPriority < 50) return (
                                <div className="text-xs text-gray-400 italic py-1">You cannot approve your own gate request</div>
                              );
                              return (
                              <div className="space-y-2">
                                <textarea
                                  value={approveRejectNotes}
                                  onChange={e => setApproveRejectNotes(e.target.value)}
                                  placeholder="Add notes (optional)..."
                                  className="w-full text-xs border border-gray-200 rounded p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                                  rows={2}
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={async () => {
                                      try {
                                        await stageGateAPI.approve(gate._id, approveRejectNotes || undefined);
                                        setApproveRejectNotes('');
                                        fetchGatesAndTests();
                                      } catch {}
                                    }}
                                    className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await stageGateAPI.reject(gate._id, approveRejectNotes || undefined);
                                        setApproveRejectNotes('');
                                        fetchGatesAndTests();
                                      } catch {}
                                    }}
                                    className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                                  >
                                    Reject
                                  </button>
                                </div>
                              </div>
                              );
                            })()}

                            {/* Testing gate: show linked test cases */}
                            {gate.gateType === 'testing' && (
                              <div className="space-y-2">
                                {gateTestCases.length > 0 && (
                                  <div className="space-y-1.5">
                                    {gateTestCases.map((tc: any) => {
                                      const isExecuting = executeTestId === tc._id;
                                      return (
                                        <div key={tc._id} className={`border rounded p-2 text-xs ${
                                          tc.status === 'passed' ? 'border-green-200 bg-green-50' :
                                          tc.status === 'failed' ? 'border-red-200 bg-red-50' :
                                          'border-gray-200 bg-white'
                                        }`}>
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              {tc.status === 'passed' ? (
                                                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                              ) : tc.status === 'failed' ? (
                                                <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                              ) : (
                                                <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                                              )}
                                              <span className="font-medium text-gray-800">{tc.title}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              {(tc.status === 'pending' || tc.status === 'in_progress' || tc.status === 'failed') && (
                                                <button
                                                  onClick={() => setExecuteTestId(isExecuting ? null : tc._id)}
                                                  className="text-[10px] font-medium text-blue-600 hover:text-blue-700 px-1.5 py-0.5 rounded hover:bg-blue-50"
                                                >
                                                  {tc.status === 'failed' ? 'Retest' : 'Execute'}
                                                </button>
                                              )}
                                            </div>
                                          </div>

                                          {tc.description && (
                                            <div className="text-gray-500 mt-1 ml-6">{tc.description}</div>
                                          )}

                                          {tc.steps && tc.steps.length > 0 && (
                                            <div className="mt-1.5 ml-6 space-y-0.5">
                                              {tc.steps.map((s: any, si: number) => (
                                                <div key={si} className="flex gap-2">
                                                  <span className="text-gray-400 shrink-0">{si + 1}.</span>
                                                  <div>
                                                    <span className="text-gray-700">{s.step}</span>
                                                    {s.expectedResult && (
                                                      <span className="text-gray-400 ml-1">→ {s.expectedResult}</span>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {tc.executionHistory && tc.executionHistory.length > 0 && !isExecuting && (
                                            <div className="mt-1.5 ml-6 space-y-1">
                                              {tc.executionHistory.length > 1 && (
                                                <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{tc.executionHistory.length} attempts</div>
                                              )}
                                              {tc.executionHistory.map((run: any, ri: number) => (
                                                <div key={ri} className={`text-xs rounded p-1.5 ${run.status === 'passed' ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                                                  <div className="flex items-center gap-1.5">
                                                    <span className={`font-medium ${run.status === 'passed' ? 'text-green-700' : 'text-red-700'}`}>{run.status === 'passed' ? 'Passed' : 'Failed'}</span>
                                                    <span className="text-gray-400">{run.executedBy?.name}{run.executedAt && ` · ${new Date(run.executedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`}</span>
                                                  </div>
                                                  {run.resultNotes && <div className="text-gray-600 mt-0.5">{run.resultNotes}</div>}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          {tc.resultNotes && !isExecuting && (!tc.executionHistory || tc.executionHistory.length === 0) && (
                                            <div className="mt-1.5 ml-6 text-gray-600 bg-gray-50 rounded p-1.5">
                                              <span className="font-medium">Result:</span> {tc.resultNotes}
                                              {tc.executedBy?.name && (
                                                <span className="text-gray-400"> — {tc.executedBy.name}{tc.executedAt && `, ${new Date(tc.executedAt).toLocaleDateString()}`}</span>
                                              )}
                                            </div>
                                          )}

                                          {isExecuting && (
                                            <div className="mt-2 ml-6 space-y-2 border-t pt-2">
                                              <textarea
                                                value={executeNotes}
                                                onChange={e => setExecuteNotes(e.target.value)}
                                                placeholder="Result notes — describe what happened..."
                                                className="w-full text-xs border border-gray-200 rounded p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                rows={2}
                                              />
                                              <div className="flex gap-2">
                                                <button
                                                  onClick={async () => {
                                                    try {
                                                      await testCaseAPI.execute(tc._id, { status: 'passed', resultNotes: executeNotes });
                                                      setExecuteTestId(null);
                                                      setExecuteNotes('');
                                                      fetchGatesAndTests();
                                                    } catch {}
                                                  }}
                                                  className="px-2 py-1 text-[11px] bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                                >
                                                  Pass
                                                </button>
                                                <button
                                                  onClick={async () => {
                                                    try {
                                                      await testCaseAPI.execute(tc._id, { status: 'failed', resultNotes: executeNotes });
                                                      setExecuteTestId(null);
                                                      setExecuteNotes('');
                                                      fetchGatesAndTests();
                                                    } catch {}
                                                  }}
                                                  className="px-2 py-1 text-[11px] bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                                                >
                                                  Fail
                                                </button>
                                                <button
                                                  onClick={() => { setExecuteTestId(null); setExecuteNotes(''); }}
                                                  className="px-2 py-1 text-[11px] text-gray-500 hover:text-gray-700"
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Create test case button & form */}
                                {gate.status === 'pending' && (
                                  <>
                                    {showCreateTest === gate._id ? (
                                      <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/50 space-y-2">
                                        <input
                                          value={newTest.title}
                                          onChange={e => setNewTest(prev => ({ ...prev, title: e.target.value }))}
                                          placeholder="Test case title"
                                          className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />
                                        <textarea
                                          value={newTest.description}
                                          onChange={e => setNewTest(prev => ({ ...prev, description: e.target.value }))}
                                          placeholder="Description (optional)"
                                          className="w-full text-xs border border-gray-200 rounded p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                                          rows={2}
                                        />

                                        <div>
                                          <div className="text-xs font-medium text-gray-600 mb-1">Test Steps</div>
                                          {newTest.steps.map((s, si) => (
                                            <div key={si} className="flex gap-1 mb-1">
                                              <span className="text-xs text-gray-400 mt-1.5 shrink-0">{si + 1}.</span>
                                              <input
                                                value={s.step}
                                                onChange={e => {
                                                  const steps = [...newTest.steps];
                                                  steps[si] = { ...steps[si], step: e.target.value };
                                                  setNewTest(prev => ({ ...prev, steps }));
                                                }}
                                                placeholder="Step"
                                                className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                              />
                                              <input
                                                value={s.expectedResult}
                                                onChange={e => {
                                                  const steps = [...newTest.steps];
                                                  steps[si] = { ...steps[si], expectedResult: e.target.value };
                                                  setNewTest(prev => ({ ...prev, steps }));
                                                }}
                                                placeholder="Expected result"
                                                className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                              />
                                              {newTest.steps.length > 1 && (
                                                <button
                                                  onClick={() => setNewTest(prev => ({ ...prev, steps: prev.steps.filter((_, i) => i !== si) }))}
                                                  className="text-red-400 hover:text-red-600 text-xs px-1"
                                                >x</button>
                                              )}
                                            </div>
                                          ))}
                                          <button
                                            onClick={() => setNewTest(prev => ({ ...prev, steps: [...prev.steps, { step: '', expectedResult: '' }] }))}
                                            className="text-[10px] text-blue-600 hover:text-blue-700 font-medium mt-0.5"
                                          >+ Add step</button>
                                        </div>

                                        <div>
                                          <div className="text-xs font-medium text-gray-600 mb-1">Assign to</div>
                                          <select
                                            value={newTest.assignedToUserId}
                                            onChange={e => {
                                              const u = users.find(u => u._id === e.target.value);
                                              setNewTest(prev => ({ ...prev, assignedToUserId: e.target.value, assignedToName: u?.name || '' }));
                                            }}
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                          >
                                            <option value="">{gate.assignedTo?.name ? `${gate.assignedTo.name} (gate assignee)` : 'Select tester...'}</option>
                                            {users.filter(u => u._id !== gate.assignedTo?.userId?.toString()).map(u => (
                                              <option key={u._id} value={u._id}>{u.name}</option>
                                            ))}
                                          </select>
                                        </div>

                                        <div className="flex gap-2 pt-1">
                                          <button
                                            onClick={async () => {
                                              const aId = newTest.assignedToUserId || gate.assignedTo?.userId?.toString();
                                              const aName = newTest.assignedToName || gate.assignedTo?.name || '';
                                              if (!newTest.title || !aId || !task) return;
                                              try {
                                                await testCaseAPI.create({
                                                  title: newTest.title,
                                                  description: newTest.description || undefined,
                                                  taskId: task._id,
                                                  projectId: task.projectId,
                                                  stageGateRequestId: gate._id,
                                                  steps: newTest.steps.filter(s => s.step),
                                                  assignedToUserId: aId,
                                                  assignedToName: aName,
                                                });
                                                setNewTest({ title: '', description: '', assignedToUserId: '', assignedToName: '', steps: [{ step: '', expectedResult: '' }] });
                                                setShowCreateTest(null);
                                                fetchGatesAndTests();
                                              } catch {}
                                            }}
                                            disabled={!newTest.title || (!newTest.assignedToUserId && !gate.assignedTo?.userId)}
                                            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-medium disabled:opacity-50"
                                          >
                                            Create Test Case
                                          </button>
                                          <button
                                            onClick={() => {
                                              setShowCreateTest(null);
                                              setNewTest({ title: '', description: '', assignedToUserId: '', assignedToName: '', steps: [{ step: '', expectedResult: '' }] });
                                            }}
                                            className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => setShowCreateTest(gate._id)}
                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Add Test Case
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Standalone test cases (not linked to a gate) */}
              {taskTestCases.filter((tc: any) => !tc.stageGateRequestId).length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-gray-500 mb-1">Standalone Test Cases</div>
                  {taskTestCases.filter((tc: any) => !tc.stageGateRequestId).map((tc: any) => {
                    const isExecuting = executeTestId === tc._id;
                    return (
                      <div key={tc._id} className={`border rounded-lg px-3 py-2 text-sm ${
                        tc.status === 'passed' ? 'border-green-200 bg-green-50' :
                        tc.status === 'failed' ? 'border-red-200 bg-red-50' :
                        'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {tc.status === 'passed' ? (
                              <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            ) : tc.status === 'failed' ? (
                              <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            ) : (
                              <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                            )}
                            <span className="font-medium">{tc.title}</span>
                          </div>
                          {(tc.status === 'pending' || tc.status === 'in_progress' || tc.status === 'failed') && (
                            <button
                              onClick={() => setExecuteTestId(isExecuting ? null : tc._id)}
                              className="text-xs font-medium text-blue-600 hover:text-blue-700"
                            >
                              {tc.status === 'failed' ? 'Retest' : 'Execute'}
                            </button>
                          )}
                        </div>
                        {tc.assignedTo?.name && (
                          <div className="text-xs mt-1 text-gray-500">QA: {tc.assignedTo.name}</div>
                        )}
                        {tc.executionHistory && tc.executionHistory.length > 0 && !isExecuting && (
                          <div className="mt-1.5 space-y-1">
                            {tc.executionHistory.length > 1 && (
                              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{tc.executionHistory.length} attempts</div>
                            )}
                            {tc.executionHistory.map((run: any, ri: number) => (
                              <div key={ri} className={`text-xs rounded p-1.5 ${run.status === 'passed' ? 'bg-green-50/80 border border-green-100' : 'bg-red-50/80 border border-red-100'}`}>
                                <span className={`font-medium ${run.status === 'passed' ? 'text-green-700' : 'text-red-700'}`}>{run.status === 'passed' ? 'Passed' : 'Failed'}</span>
                                <span className="text-gray-400 ml-1">{run.executedBy?.name}{run.executedAt && ` · ${new Date(run.executedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}</span>
                                {run.resultNotes && <div className="text-gray-600 mt-0.5">{run.resultNotes}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                        {tc.resultNotes && !isExecuting && (!tc.executionHistory || tc.executionHistory.length === 0) && (
                          <div className="text-xs mt-1.5 text-gray-600 bg-white/60 rounded p-1.5">
                            <span className="font-medium">Result:</span> {tc.resultNotes}
                            {tc.executedBy?.name && (
                              <span className="text-gray-400"> — {tc.executedBy.name}</span>
                            )}
                          </div>
                        )}
                        {isExecuting && (
                          <div className="mt-2 space-y-2 border-t pt-2">
                            <textarea
                              value={executeNotes}
                              onChange={e => setExecuteNotes(e.target.value)}
                              placeholder="Result notes — describe what happened..."
                              className="w-full text-xs border border-gray-200 rounded p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    await testCaseAPI.execute(tc._id, { status: 'passed', resultNotes: executeNotes });
                                    setExecuteTestId(null); setExecuteNotes('');
                                    fetchGatesAndTests();
                                  } catch {}
                                }}
                                className="px-2 py-1 text-[11px] bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                              >Pass</button>
                              <button
                                onClick={async () => {
                                  try {
                                    await testCaseAPI.execute(tc._id, { status: 'failed', resultNotes: executeNotes });
                                    setExecuteTestId(null); setExecuteNotes('');
                                    fetchGatesAndTests();
                                  } catch {}
                                }}
                                className="px-2 py-1 text-[11px] bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                              >Fail</button>
                              <button onClick={() => { setExecuteTestId(null); setExecuteNotes(''); }} className="px-2 py-1 text-[11px] text-gray-500 hover:text-gray-700">Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Blockers */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Blockers</h3>

            {(task.blockers ?? []).length > 0 && (
              <div className="space-y-1.5 mb-3">
                {(task.blockers ?? []).map((blocker: any, idx: number) => (
                  <div key={idx} className={`border rounded-lg px-3 py-2 text-sm ${blocker.resolvedAt ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                          blocker.type === 'task' ? 'bg-blue-100 text-blue-700' :
                          blocker.type === 'feature' ? 'bg-teal-100 text-teal-700' :
                          blocker.type === 'bug' ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>{blocker.type}</span>
                        <span className="truncate text-gray-700">{blocker.description}</span>
                        {blocker.resolvedAt && <span className="text-xs text-green-600 shrink-0">Resolved</span>}
                      </div>
                      {!blocker.resolvedAt && (
                        <button
                          onClick={async () => {
                            try {
                              await taskAPI.resolveBlocker(id!, idx);
                              fetchTaskDetails();
                            } catch {}
                          }}
                          className="text-xs text-green-600 hover:text-green-800 font-medium shrink-0"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!id || !blockerForm.description.trim()) return;
              setBlockerSubmitting(true);
              try {
                await taskAPI.addBlocker(id, { type: blockerForm.type, description: blockerForm.description });
                setBlockerForm({ type: 'external', description: '' });
                fetchTaskDetails();
              } catch {}
              setBlockerSubmitting(false);
            }} className="flex gap-2">
              <select
                value={blockerForm.type}
                onChange={e => setBlockerForm(f => ({ ...f, type: e.target.value }))}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none"
              >
                <option value="task">Task</option>
                <option value="feature">Feature</option>
                <option value="bug">Bug</option>
                <option value="external">External</option>
              </select>
              <input
                value={blockerForm.description}
                onChange={e => setBlockerForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What's blocking this task?"
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={blockerSubmitting || !blockerForm.description.trim()}
                className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Add
              </button>
            </form>
          </div>

          {/* Attachments */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Attachments {task.attachments && task.attachments.length > 0 && (
                  <span className="text-gray-500 font-normal">({task.attachments.length})</span>
                )}
              </h3>
              <button
                onClick={() => setShowAttachmentModal(true)}
                disabled={uploadingFile}
                className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                {uploadingFile ? 'Uploading...' : '+ Add attachment'}
              </button>
            </div>

            {/* Attachment Modal */}
            {showAttachmentModal && (
              <div className="!mt-0 fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add Attachment</h3>
                    <button
                      onClick={() => {
                        setShowAttachmentModal(false);
                        setAttachmentLink('');
                        setAttachmentName('');
                        setAttachmentType('link');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Toggle between file and link */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setAttachmentType('file')}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded ${
                        attachmentType === 'file'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload File
                    </button>
                    <button
                      onClick={() => setAttachmentType('link')}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded ${
                        attachmentType === 'link'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Add Link
                    </button>
                  </div>

                  {/* File Upload */}
                  {attachmentType === 'file' && (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFile}
                        className="w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50"
                      >
                        <div className="text-center">
                          <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-sm text-gray-600">
                            {uploadingFile ? 'Uploading...' : 'Click to select a file'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Any file type supported
                          </p>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* Link Input */}
                  {attachmentType === 'link' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Link URL *
                        </label>
                        <input
                          type="url"
                          value={attachmentLink}
                          onChange={(e) => setAttachmentLink(e.target.value)}
                          placeholder="https://example.com/document.pdf"
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Display Name (optional)
                        </label>
                        <input
                          type="text"
                          value={attachmentName}
                          onChange={(e) => setAttachmentName(e.target.value)}
                          placeholder="Document name"
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setShowAttachmentModal(false);
                            setAttachmentLink('');
                            setAttachmentName('');
                          }}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleLinkSubmit}
                          disabled={uploadingFile || !attachmentLink.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Add Link
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {task.attachments && task.attachments.length > 0 && (
              <div className="space-y-2">
                {task.attachments.map((attachment, idx) => (
                  <div key={idx} className="group">
                    {attachment.type === 'image' ? (
                      <div className="p-3 bg-gray-50 rounded hover:bg-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm text-gray-700 truncate flex-1">{attachment.name}</span>
                          <span className="text-xs text-gray-400">{new Date(attachment.uploadedAt).toLocaleDateString()}</span>
                          <button onClick={() => handleDeleteAttachment(attachment.url)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 rounded transition-opacity" title="Delete">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                        <img src={resolveFileUrl(attachment.url)} alt={attachment.name}
                          onClick={() => setLightboxUrl(resolveFileUrl(attachment.url))}
                          className="max-w-full max-h-48 rounded-lg border border-gray-200 object-cover cursor-pointer hover:opacity-90 transition-opacity" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded hover:bg-gray-100">
                        <div className="flex-shrink-0">
                          {attachment.type === 'document' ? (
                            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                          ) : (
                            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <a href={resolveFileUrl(attachment.url)} target="_blank" rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 hover:underline block truncate">{attachment.name}</a>
                          <div className="text-xs text-gray-500">{attachment.type} • {new Date(attachment.uploadedAt).toLocaleDateString()}</div>
                        </div>
                        <a href={resolveFileUrl(attachment.url)} download={attachment.name}
                          className="opacity-0 group-hover:opacity-100 p-1 text-blue-600 hover:bg-blue-50 rounded transition-opacity" title="Download" onClick={e => e.stopPropagation()}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        </a>
                        <button onClick={() => handleDeleteAttachment(attachment.url)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 rounded transition-opacity" title="Delete attachment">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(!task.attachments || task.attachments.length === 0) && (
              <p className="text-sm text-gray-500">No attachments yet</p>
            )}
          </div>
        </div>

        {/* Activity Sidebar */}
        <div className="w-80 border-l border-gray-200 bg-gray-50/50 shrink-0 hidden lg:block">
          <div className="sticky top-14 p-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Activity</span>
              <span className="text-[10px] text-gray-400">{comments.length}</span>
            </div>

            {/* Add Comment */}
            <form onSubmit={handleAddComment} className="mb-3">
              <textarea
                value={newComment}
                onChange={(e) => { setNewComment(e.target.value); setErrors(prev => ({ ...prev, comment: '' })); }}
                placeholder="Write a comment..."
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 resize-none"
                rows={2}
              />
              {errors.comment && <p className="mt-1 text-[10px] text-red-600">{errors.comment}</p>}
              <div className="mt-1.5 flex justify-end">
                <button type="submit" disabled={isAddingComment}
                  className="text-[10px] text-blue-600 hover:text-blue-700 disabled:text-gray-300 font-medium">
                  {isAddingComment ? 'Adding...' : 'Comment'}
                </button>
              </div>
            </form>

            {/* Comments List */}
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment._id} className="text-sm">
                  <div className="flex items-start gap-2 mb-1">
                    <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {comment.author.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{comment.author.name}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(comment.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-700 mt-1 whitespace-pre-wrap break-words">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-8">No comments yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {confirmModal.show && task && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="font-semibold text-gray-900">
              {confirmModal.isDone ? 'Complete Task' : 'Revert Task'}
            </h3>
            <p className="text-sm text-gray-600">
              {confirmModal.isDone
                ? `Mark "${task.title}" as done?`
                : `Move "${task.title}" back to ${confirmModal.newStatus.replace('_', ' ')}?`
              }
            </p>
            {confirmModal.isDone && (task.storyPoints || 0) > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700 font-medium">+{task.storyPoints} story points</p>
                <p className="text-xs text-green-600 mt-0.5">Points will be recorded in your journal.</p>
              </div>
            )}
            {confirmModal.isRevert && (task.storyPoints || 0) > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700 font-medium">-{task.storyPoints} story points will be deducted</p>
                <p className="text-xs text-yellow-600 mt-0.5">Points will be re-earned when you complete the task again.</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmModal({ show: false, newStatus: '', isDone: false, isRevert: false })} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                onClick={confirmStatusChange}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${confirmModal.isDone ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-500 hover:bg-yellow-600'}`}
              >
                {confirmModal.isDone ? 'Mark as Done \u2713' : 'Revert & Deduct'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] cursor-zoom-out" onClick={() => setLightboxUrl(null)}>
          <button onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <img src={lightboxUrl} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
      </div>
    </div>
  );
};

export default TaskDetail;
